import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from "@angular/core";
import { NonNullableFormBuilder, ValidationErrors, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { map, take } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { GroupAddressWithExtensions } from "../groupsPage/groups-page.component";
import { CheckListItem } from "@onsip/web/features/shared/components/onsipItemList/checkList/check-item-list.component";
import { GroupAddressService } from "@onsip/common/services/api/resources/groupAddress/group-address.service";
import { ModalMaterialComponent } from "@onsip/web/features/shared/components/modal/modal-material.component";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { AddressSummary } from "../../../../../common/services/api/resources/addressSummary/address-summary.service";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { ExtensionsListComponent } from "@onsip/web/features/shared/components/extensionsList/extensions-list.component";
import { E2eLocators } from "@onsip/e2e/configs/locators";
import { NAME_VALIDATOR_REGEX } from "@onsip/common/libraries/validators/name-validators";

type ordering = "parallel" | "sequential";

@Component({
  selector: "onsip-group-shelf-edit",
  templateUrl: "./group-shelf-edit.component.html",
  styleUrls: ["./group-shelf-edit.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class GroupShelfEditComponent implements OnInit, OnDestroy {
  @ViewChild("extensionsList") extensionsListRef!: ExtensionsListComponent;
  @Input() group!: GroupAddressWithExtensions;
  @Output() viewPanel = new EventEmitter<void>();

  groupEditInfo = this.formBuilder.group({
    name: this.formBuilder.control("", {
      validators: [Validators.required, Validators.pattern(NAME_VALIDATOR_REGEX)]
    }),
    defaultAddress: "",
    ordering: "",
    timeout: 0,
    sipAddress: "",
    extensions: this.formBuilder.control<Array<string>>([])
  });

  durationArr: Array<number> = [];
  membersData: Array<CheckListItem> = [];
  addressSummaries: Array<AddressSummary> = [];
  private unsubscriber = new Subscription();
  mobileView!: Observable<boolean>;
  E2eLocators = E2eLocators;
  loadingStateComplete$ = new BehaviorSubject(true);

  constructor(
    private formBuilder: NonNullableFormBuilder,
    private translate: TranslateService,
    private groups: GroupAddressService,
    private dialog: MatDialog,
    private snackbar: SnackbarService,
    private breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit() {
    this.mobileView = this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium])
      .pipe(map(res => res.matches));

    // Ring duration data preparation
    for (let i = 0; i < 24; i++) {
      this.durationArr.push((i + 1) * 5);
    }

    this.groupEditInfo.patchValue({
      name: this.group.address.name,
      ordering: this.group.ordering,
      timeout: this.group.timeout,
      sipAddress: this.group.address.username,
      extensions: this.group.extensions,
      defaultAddress: this.group.defaultAddress
        ? `${this.group.defaultAddress.username}@${this.group.defaultAddress.domain}`
        : ""
    });
  }

  getErrorMessage(errors: ValidationErrors | null): string {
    if (errors?.invalidExtension || errors?.minLength) {
      return this.translate.instant(
        "ONSIP_I18N.EXTENSIONS_MUST_BE_BETWEEN_26_DIGITS_AND_BEGIN_WITH_EITHER__OR_A_NONZERO_DIGIT"
      );
    } else if (errors?.repeatExtension) {
      return this.translate.instant("ONSIP_I18N.EXTENSION_ALREADY_IN_USE");
    } else return "";
  }

  getNameError() {
    const control = this.groupEditInfo.controls.name;
    if (control.hasError("required")) {
      return this.translate.instant("ONSIP_I18N.NAME_IS_REQUIRED");
    } else if (control.hasError("pattern")) {
      return this.translate.instant(
        "ONSIP_I18N.MUST_CONTAIN_ONLY_ALPHANUMERIC_CHARACTERS_SPACES_AND_NO_PLUS_COMMA_COLON"
      );
    }
    return "";
  }

  trackByItemId(id: number, item: { id: string }): string {
    return item.id;
  }

  onSubmit(save: boolean) {
    if (save) {
      if (this.groupEditInfo.invalid) {
        return;
      }

      this.loadingStateComplete$.next(false);

      const groupValue = this.groupEditInfo.getRawValue();
      const params = {
        Address: `${this.group.address.username}@${this.group.address.domain}`,
        DefaultAddress: groupValue.defaultAddress,
        Name: groupValue.name,
        Ordering: groupValue.ordering as ordering,
        Timeout: groupValue.timeout,
        Username: groupValue.sipAddress,
        SelectedAddresses: this.group.groupMemberAddresses.map(
          member => `${member.address.username}@${member.address.domain}`
        )
      };
      this.groups.groupAddressEdit(params)?.then(res => {
        if (res && res.status === "success") {
          this.extensionsListRef.editExtensions(this.group.address.username);

          this.snackbar.openSnackBar(
            this.translate.instant("ONSIP_I18N.YOUR_CHANGES_HAVE_BEEN_SAVED"),
            "success"
          );
          this.viewPanel.next();
        } else {
          this.snackbar.openSnackBar(res.data.message, "error");
          this.groups.clearErrors();
        }
        this.loadingStateComplete$.next(true);
      });
    } else {
      const modal = this.dialog.open(ModalMaterialComponent, {
        panelClass: ["mat-typography", "onsip-dialog-universal-style"],
        data: {
          title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
          message: this.translate.instant("ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"),
          primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
          secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
          primaryBtnFlat: true
        }
      });

      this.unsubscriber.add(
        modal
          .afterClosed()
          .pipe(take(1))
          .subscribe(retObj => {
            if (retObj && retObj.doPrimaryAction) {
              this.viewPanel.emit();
            }
          })
      );
    }
  }

  ngOnDestroy() {
    this.unsubscriber.unsubscribe();
  }
}
